/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import clubTablePosition from "utils/tableHelper/playerPosition";
import ContentShell from "components/ContentShell";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import { getPositions } from "services/playerCardService";
import usePaginationHook from "../../../hooks/usePagination";
import { JSONSearch, opacity2hex, sortArray } from "utils/miscUtils";
import SearchInput from "components/SearchInput";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { useSnackbar } from "hooks/useSnackbar";
import CustomButton from "components/CustomButton";
import EditIcon from "@mui/icons-material/Edit";

const useStyles = makeStyles((theme) => ({
  savecancel: {
    width: "160px",
    height: 40,
  },
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "20px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  searchInput: {
    width: "270px",
    marginBottom: 20,
  },
}));

const RowActions = ({ data }: any) => {
  const navigate = useNavigate();
  const handleRowAction = (event: any, data: any) => {
    event.stopPropagation();
    navigate(
      configData.routes.playerDetails.editPosition?.replace(":id", data?._id),
    );
  };

  return (
    <CustomButton
      onClick={(e: any) => handleRowAction(e, data)}
      startIcon={<EditIcon />}
    >
      Edit
    </CustomButton>
  );
};

const PositionsList = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const styles = useStyles();
  const [columns, setColumns] = useState([]);
  const [fullList, setFullList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [searchValue, setSearchValue] = useState("");
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.POSITION.UPDATE_ROLES,
  );

  const { pageNo, pageSize } = usePaginationHook({
    totalItems: filteredList.length,
  });

  const handleCreatePosition = () => {
    navigate(configData.routes.playerDetails.createPosition);
  };

  const fetchData = async (search) => {
    setLoading(true);
    try {
      const response = await getPositions({ pageNo, pageSize, search });
      const data = response.data.map((item) => {
        const cleanedItem = Object.keys(item).reduce((acc, key) => {
          acc[key] = item[key] ?? "---";
          return acc;
        }, {});
        return cleanedItem;
      });
      showSnackbar(response.message, "success");
      setFullList(data);
      setFilteredList(data);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message);
      setLoading(false);
      console.error("Error fetching position data: ", error);
    }
  };

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.playerDetails.editPosition?.replace(":id", data?._id)
      : null;

  const onColumnClick = (column: any) => {
    let _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onSearch = (value: any) => {
    setSearchValue(value);
    if (value === "") {
      setFilteredList(fullList);
    }

    setFilteredList(JSONSearch(fullList, ["code", "name"], value));
  };

  useEffect(() => {
    const initialColumns = clubTablePosition().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(initialColumns);

    fetchData(searchValue);
  }, [pageNo, pageSize, hasEditPermission]);

  return (
    <ContentShell
      loading={loading}
      title="Positions"
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a position"
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.POSITION.CREATE_ROLES) && (
            <CustomButton onClick={handleCreatePosition}>Create</CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList}
          actions={RowActions}
          onColumnClick={onColumnClick}
          rowHref={rowHref}
          totalEntries={filteredList.length}
          highlightAltRow={true}
        />
      </Box>
    </ContentShell>
  );
};

export default PositionsList;
